// istanbul ignore file
import { useCallback, useMemo } from 'react';

import * as React from 'react';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import { generateTheme } from '@cld/theme-helper';
import { lightTheme as light } from '@clds/themes';
import { ServerError } from '@cld/empty-state';
import GlobalStyle from '@cld/global-style';
import { SessionServiceProvider } from '@cld/session-service';
import { usePhone, useTablet } from '@clds/style-utils';
import {
  AppId,
  UnicornWrapper,
  CustomerIdProviderWithSession,
  UserDataProvider,
  ApplicationWrapperLanguageProvider,
} from '@cld-remote/application_wrapper/index';

import { CloudinaryFavicon } from './favicon';
import { loadMessages } from './i18n';
import { AppConnected } from './app-connected';
import { AppWrapper } from './app-wrapper';
import { UnicornApi, createUseUnicornTreatment } from '@cld/unicorn-api';
import { LoaderSpinner } from './components/loaderSpinner';
import { HeapConfigurator } from './heap-configurator';
import { TreatmentValue } from './types/treatments';

export const basename = '/pm';

const AppGlobalStyle = createGlobalStyle`
  html, body, #root {
    height: 100%;
  }

  /* console overrides: start */
  body {
    font-size: 12px;
    min-width: 320px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  // adaptive images
  img {
    max-width: 100%;
    height: auto;
  }

  /* console overrides: end */

  .non-clickable-tooltip {
    pointer-events: none;
  }
`;

const ABTreatmentLoader = ({
  unicornApi,
  children,
}: {
  unicornApi: UnicornApi;
  children: (childrenTreatments: {
    optimizationReportsLinkTreatment: TreatmentValue;
    mediaExplorerDeprecationTreatment: TreatmentValue;
  }) => React.JSX.Element;
}): React.JSX.Element | null => {
  const useUnicornTreatment = useMemo(() => createUseUnicornTreatment(unicornApi.methods.getAbTestTreatment), [unicornApi.methods.getAbTestTreatment]);

  const optimizationReportsLinkTreatment = useUnicornTreatment({
    assignedTo: 'account',
    testId: 'PM-Optimization-Reports',
    supportedTreatments: ['on', 'off'],
    errorFallbackTreatment: 'off',
  });

  const mediaExplorerDeprecationTreatment = useUnicornTreatment({
    assignedTo: 'account',
    testId: 'MM-PM-Media-Explorer-deprecated',
    supportedTreatments: ['on', 'off'],
    errorFallbackTreatment: 'off',
    attributes: {
      media_explorer_disabled: unicornApi.data.capabilities.includes('mediaExplorerDisabled'),
    },
  });

  if (optimizationReportsLinkTreatment === 'loading' || mediaExplorerDeprecationTreatment === 'loading') {
    return <LoaderSpinner />;
  }

  return children({
    optimizationReportsLinkTreatment,
    mediaExplorerDeprecationTreatment,
  });
};

export const App = () => {
  const theme = useMemo(() => generateTheme(light), []);
  const isPhone = usePhone();
  const isTablet = useTablet();

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AppGlobalStyle />
      <CloudinaryFavicon />

      <SessionServiceProvider appId={AppId.MediaAPI}>
        <UnicornWrapper>
          {(unicornApiState) => (
            <>
              {unicornApiState.status === 'loading' && <LoaderSpinner />}
              {unicornApiState.status === 'error' && <ServerError />}
              {unicornApiState.status === 'ready' && (
                <HeapConfigurator unicornApi={unicornApiState.api}>
                  <ABTreatmentLoader unicornApi={unicornApiState.api}>
                    {({ optimizationReportsLinkTreatment, mediaExplorerDeprecationTreatment }) => (
                      <CustomerIdProviderWithSession>
                        <UserDataProvider>
                          <ApplicationWrapperLanguageProvider loadAppMessages={loadMessages}>
                            <AppWrapper
                              isMobile={isTablet || isPhone}
                              unicornApi={unicornApiState.api}
                              optimizationReportsLinkTreatment={optimizationReportsLinkTreatment}
                              mediaExplorerDeprecationTreatment={mediaExplorerDeprecationTreatment}
                            >
                              <AppConnected unicornApi={unicornApiState.api} mediaExplorerDeprecationTreatment={mediaExplorerDeprecationTreatment} />
                            </AppWrapper>
                          </ApplicationWrapperLanguageProvider>
                        </UserDataProvider>
                      </CustomerIdProviderWithSession>
                    )}
                  </ABTreatmentLoader>
                </HeapConfigurator>
              )}
            </>
          )}
        </UnicornWrapper>
      </SessionServiceProvider>
    </ThemeProvider>
  );
};
